import { useMutation } from '@tanstack/react-query';

import Axios from 'axios';
import { queryClient } from 'QueryClientProvider';

import { useAdjustTagUsageCount } from '@hooks/CachedLists/UseTagCatalog';

const CONTACT_LIST_QUERY_KEYS = ['models', 'models-management', 'customers'];

const tagMatches = (itemTag, tag) => {
    if (tag.ID != null && itemTag.ID != null && Number(itemTag.ID) === Number(tag.ID)) {
        return true;
    }
    if (tag.Name && itemTag.Name && itemTag.Name.toLowerCase() === tag.Name.toLowerCase()) {
        return true;
    }
    return false;
};

// I filtri della wall usano T e _normalizedTagNames della cache lista, non contact.Tags.
const patchContactTagsInCachedLists = (contactID, nextTagsFromCurrent) => {
    CONTACT_LIST_QUERY_KEYS.forEach((queryKey) => {
        queryClient.setQueryData([queryKey], (old) => {
            if (!Array.isArray(old)) {
                return old;
            }
            const index = old.findIndex((item) => Number(item.ID) === Number(contactID));
            if (index === -1) {
                return old;
            }
            const item = old[index];
            const nextT = nextTagsFromCurrent(item.T || []);
            const nextList = [...old];
            nextList[index] = {
                ...item,
                T: nextT,
                _normalizedTagNames: nextT.map((listTag) => listTag.Name.toLowerCase()),
            };
            return nextList;
        });
    });
};

const addTagToList = (currentT, tag) => {
    if (currentT.some((itemTag) => tagMatches(itemTag, tag))) {
        return currentT;
    }
    return [...currentT, { ID: tag.ID, Name: tag.Name }];
};

const removeTagFromList = (currentT, tag) => currentT.filter((itemTag) => !tagMatches(itemTag, tag));

const updateTags = async (ID) => {
    Axios.get(`/contacts/tags/${ID}`).then((data) => {
        queryClient.setQueryData(['contact', ID], (old) => ({
            ...old,
            Tags: data,
        }));
    });
};

const addContactTag = ({ tag, contactID }) => Axios.post(`/contacts/addtag/${contactID}/${tag.ID}`);

const useAddContactTag = () => {
    const adjustTagUsageCount = useAdjustTagUsageCount();
    return useMutation({
        mutationFn: addContactTag,
        onSuccess: (data, variables) => {
            updateTags(variables.contactID);
            patchContactTagsInCachedLists(variables.contactID, (currentT) => addTagToList(currentT, variables.tag));
            adjustTagUsageCount(variables.tag.ID, 1);
        },
    });
};

const removeContactTag = async ({ tag, contactID }) => Axios.post(`/contacts/removetag/${contactID}/${tag.ID}`);

const useRemoveContactTag = () => {
    const adjustTagUsageCount = useAdjustTagUsageCount();
    return useMutation({
        mutationFn: removeContactTag,
        onSuccess: (data, variables) => {
            const model = queryClient.getQueryData(['contact', variables.contactID]);
            const newTags = model.Tags.filter(function (obj) {
                return obj.ID !== variables.tag.ID;
            });
            queryClient.setQueryData(['contact', variables.contactID], (old) => ({
                ...old,
                Tags: newTags,
            }));
            patchContactTagsInCachedLists(variables.contactID, (currentT) =>
                removeTagFromList(currentT, variables.tag)
            );
            adjustTagUsageCount(variables.tag.ID, -1);
        },
    });
};

export { updateTags, useAddContactTag, useRemoveContactTag };
